// src/redux/slices/messageCountSlice.js
import { createSlice } from '@reduxjs/toolkit';

const messageCountSlice = createSlice({
  name: 'messageCount',
  initialState: {
    totalCount: 0,
    threads: {}, // { threadId: count }
    roleBased: {
      account_executive: 0,
      company_admin: 0,
      worker: 0,
      super_admin: 0,
    },
    isLoading: false,
    error: null,
  },
  reducers: {
    // Update count - can increase or decrease based on action payload
    updateCount: (state, action) => {
      const { type, threadId, amount = 1 } = action.payload;
      if (type === 'increment') {
        state.totalCount += amount;
        if (threadId) {
          state.threads[threadId] = (state.threads[threadId] || 0) + amount;
        }
      } else if (type === 'decrement') {
        state.totalCount = Math.max(0, state.totalCount - amount);
        if (threadId && state.threads[threadId]) {
          state.threads[threadId] = Math.max(0, state.threads[threadId] - amount);
        }
      } else if (type === 'reset') {
        state.totalCount = 0;
        state.threads = {};
        state.roleBased = {
          account_executive: 0,
          company_admin: 0,
          worker: 0,
          super_admin: 0,
        };
      }
      state.error = null;
    },

    // Set specific count value
    setCount: (state, action) => {
      state.totalCount = action.payload;
      state.error = null;
    },

    // Set unread counts with full structure (total, threads, roleBased)
    setUnreadCounts: (state, action) => {
      const { total, threads, roleBased } = action.payload;
      if (total !== undefined) state.totalCount = total;
      if (threads !== undefined) state.threads = threads;
      if (roleBased !== undefined) {
        state.roleBased = {
          ...state.roleBased,
          ...roleBased,
        };
      }
      state.error = null;
    },

    // Update thread-specific unread count
    setThreadUnreadCount: (state, action) => {
      const { threadId, count } = action.payload;
      if (threadId) {
        const oldCount = state.threads[threadId] || 0;
        state.threads[threadId] = count;
        // Update total count
        state.totalCount = Math.max(0, state.totalCount - oldCount + count);
      }
      state.error = null;
    },

    // Clear thread unread count (when messages are read)
    clearThreadUnreadCount: (state, action) => {
      const { threadId } = action.payload;
      if (threadId && state.threads[threadId]) {
        const count = state.threads[threadId];
        state.totalCount = Math.max(0, state.totalCount - count);
        state.threads[threadId] = 0;
      }
      state.error = null;
    },

    // Reset count to 0
    resetCount: (state) => {
      state.totalCount = 0;
      state.threads = {};
      state.roleBased = {
        account_executive: 0,
        company_admin: 0,
        worker: 0,
        super_admin: 0,
      };
      state.error = null;
    },

    // Set loading state
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    // Set error state
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
  },
});

export const {
  updateCount,
  setCount,
  setUnreadCounts,
  setThreadUnreadCount,
  clearThreadUnreadCount,
  resetCount,
  setLoading,
  setError,
} = messageCountSlice.actions;

export default messageCountSlice.reducer;
